import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import {
  ScanLine, LayoutDashboard, Receipt, PiggyBank, FileText,
  FolderOpen, TrendingUp, FileSpreadsheet, Navigation, CreditCard,
  Settings, LogOut, Menu, X, ChevronRight, ShieldCheck, ScanSearch,
  Users, Mail, Lock, Clock
} from 'lucide-react';

// Plan access tiers: which plan minimum is required for each route
// 'all' = everyone, 'basic' = basic+standard, 'standard' = standard only
const navItems = [
  { to: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', minPlan: 'all' },
  { to: 'receipts', icon: Receipt, label: 'Receipts', minPlan: 'all' },
  { to: 'ocr-accuracy', icon: ScanSearch, label: 'OCR Accuracy', minPlan: 'all' },
  { to: 'budgets', icon: PiggyBank, label: 'Budgets', minPlan: 'basic' },
  { to: 'tax-reports', icon: FileText, label: 'Tax Reports', minPlan: 'basic' },
  { to: 'documents', icon: FolderOpen, label: 'Documents', minPlan: 'all' },
  { to: 'insights', icon: TrendingUp, label: 'Insights', minPlan: 'basic' },
  { to: 'invoices', icon: FileSpreadsheet, label: 'Invoices', minPlan: 'standard' },
  { to: 'mileage', icon: Navigation, label: 'Mileage', minPlan: 'basic' },
  { to: 'bills', icon: CreditCard, label: 'Bills', minPlan: 'basic' },
  { to: 'employees', icon: Users, label: 'Employees', minPlan: 'standard' },
  { to: '#', icon: Mail, label: 'Email Receipts', badge: 'Soon', minPlan: 'all' },
  { to: 'settings', icon: Settings, label: 'Settings', minPlan: 'all' },
];

const PLAN_RANK = { individual: 0, basic: 1, standard: 2 };

function hasAccess(userPlan, minPlan) {
  if (minPlan === 'all') return true;
  const rank = PLAN_RANK[userPlan] ?? 0;
  const required = PLAN_RANK[minPlan] ?? 0;
  return rank >= required;
}

function getPlanLabel(minPlan) {
  return minPlan === 'standard' ? 'Standard' : 'Basic';
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const userPlan = user?.plan || 'individual';
  // Admins get full access
  const isAdmin = user?.role === 'admin';

  const { data: trialData = {} } = useQuery({
    queryKey: ['trial-status-sidebar'],
    queryFn: async () => { const { data } = await api.get('/api/trial/status'); return data; },
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-100 flex flex-col transition-transform duration-300 lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-xl blur-sm opacity-60" />
              <div className="relative bg-gradient-to-br from-emerald-500 to-teal-600 p-2 rounded-xl">
                <ScanLine className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <span className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">ExpensIQ</span>
              <div className="text-[9px] font-medium text-slate-400 tracking-widest -mt-0.5">AUSTRALIA</div>
            </div>
          </div>
        </div>

        {/* Trial Badge */}
        {trialData.on_trial && (
          <div className="mx-3 mt-1 mb-1 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="trial-badge">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Trial — {trialData.days_remaining}d left</p>
                <p className="text-[10px] text-amber-600">{trialData.trial_plan?.charAt(0).toUpperCase()}{trialData.trial_plan?.slice(1)} plan</p>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-1">
            {navItems.map(item => {
              const accessible = isAdmin || hasAccess(userPlan, item.minPlan);
              const locked = !accessible;

              // "Soon" placeholder items
              if (item.to === '#') {
                return (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 cursor-default"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                    {item.badge && <span className="text-[10px] font-semibold text-orange-500 bg-orange-50 px-1.5 py-0.5 rounded-full ml-auto">{item.badge}</span>}
                  </div>
                );
              }

              // Locked items - show but greyed out with lock icon
              if (locked) {
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:bg-slate-50 transition-all group"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className="w-5 h-5 opacity-50" />
                    <span className="flex-1">{item.label}</span>
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                      <Lock className="w-3 h-3" />{getPlanLabel(item.minPlan)}
                    </span>
                  </NavLink>
                );
              }

              // Accessible items
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
          {/* Admin link */}
          {user?.role === 'admin' && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <NavLink
                to="admin"
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`
                }
                data-testid="nav-admin-dashboard"
              >
                <ShieldCheck className="w-5 h-5" />
                Admin Panel
              </NavLink>
            </div>
          )}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold text-sm">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-all mt-1"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
          <div className="flex items-center gap-2">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-1.5 rounded-lg">
              <ScanLine className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-emerald-600">ExpensIQ</span>
          </div>
        </header>
        <main className="min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
